import { useEffect, useState } from 'react';
import { Loader2, Wallet, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { creditsApi, type GrantCreditRequest } from '../../../lib/api/credits';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollmentId: number;
  enrollmentLabel: string;
  onSuccess?: () => void;
}

const SOURCE_OPTIONS: Array<{ value: GrantCreditRequest['sourceType']; label: string }> = [
  { value: 'manual_adjustment', label: 'Kulanz / Office-Anpassung' },
  { value: 'lesson_cancellation', label: 'Ausgefallene Unterrichtseinheit' },
  { value: 'refund', label: 'Rückerstattung' },
];

export function GrantCreditModal({ open, onOpenChange, enrollmentId, enrollmentLabel, onSuccess }: Props) {
  const [amountEur, setAmountEur] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [sourceType, setSourceType] = useState<GrantCreditRequest['sourceType']>('manual_adjustment');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmountEur('');
      setDescription('');
      setSourceType('manual_adjustment');
      setIdempotencyKey(crypto.randomUUID());
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const parsedCents = (() => {
    const normalized = amountEur.replace(',', '.').trim();
    if (normalized === '') return null;
    const num = Number(normalized);
    if (Number.isNaN(num) || num <= 0) return null;
    return Math.round(num * 100);
  })();

  const canSubmit = parsedCents !== null && description.trim() !== '' && !submitting;

  const submit = async () => {
    if (parsedCents === null) {
      setError('Bitte einen gültigen Betrag in Euro eingeben.');
      return;
    }
    if (description.trim() === '') {
      setError('Bitte einen Grund angeben.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await creditsApi.grant(enrollmentId, {
        amountCents: parsedCents,
        description: description.trim(),
        sourceType,
        idempotencyKey,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gutschrift konnte nicht gespeichert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-emerald-500" />
            Guthaben gutschreiben
          </DialogTitle>
          <DialogDescription>
            Schreibt dem Vertrag <span className="font-medium">{enrollmentLabel}</span> ein
            Guthaben gut. Der Betrag wird automatisch mit der nächsten passenden Zahlung
            verrechnet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="credit-amount">Betrag (€)</Label>
            <Input
              id="credit-amount"
              inputMode="decimal"
              placeholder="z. B. 29,00"
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              disabled={submitting}
            />
            {parsedCents !== null && (
              <p className="text-xs text-muted-foreground">
                Wird als <span className="font-medium">{(parsedCents / 100).toFixed(2).replace('.', ',')} €</span> gespeichert.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="credit-source">Art der Gutschrift</Label>
            <select
              id="credit-source"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as GrantCreditRequest['sourceType'])}
              disabled={submitting}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="credit-reason">Grund (intern sichtbar)</Label>
            <Textarea
              id="credit-reason"
              placeholder="z. B. Unterricht am 21.04. fiel aus, kein Ersatztermin"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 text-destructive text-xs p-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Abbrechen
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Gutschreiben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
